import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="submission-duplicate-check"
//
// Asks the server, as the submitter types, whether the product they are describing is already in
// the catalog, and makes them answer the question before a strong match can be submitted past.
// The candidates themselves are rendered by the server into the frame; this controller only
// decides when to ask and what a dismissal means.
export default class extends Controller {
  static targets = [
    "frame",
    "brand",
    "name",
    "url",
    "results",
    "drawerTrigger",
    "dismissal",
    "distinctionField",
    "distinction",
  ]
  static values = { url: String, delay: { type: Number, default: 300 } }

  connect() {
    this.timer = null
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
  }

  refresh() {
    if (this.timer) clearTimeout(this.timer)

    this.timer = setTimeout(() => this.load(), this.delayValue)
  }

  load() {
    if (!this.hasFrameTarget) return

    const brandId = this.hasBrandTarget ? this.brandTarget.value : ""
    const name = this.hasNameTarget ? this.nameTarget.value.trim() : ""
    const url = this.hasUrlTarget && !this.urlTarget.disabled ? this.urlTarget.value.trim() : ""

    if (!brandId || (!name && !url)) {
      this.frameTarget.removeAttribute("src")
      this.frameTarget.innerHTML = ""
      return
    }

    const endpoint = new URL(this.urlValue, window.location.origin)
    endpoint.searchParams.set("brand_id", brandId)
    endpoint.searchParams.set("product_name", name)
    endpoint.searchParams.set("submission_source_url", url)

    this.frameTarget.src = endpoint.toString()
  }

  // A submission the server sent back carries its candidates with it, so the question that
  // stopped it is put to the submitter again rather than left as a line of flash text.
  resultsTargetConnected(element) {
    if (element.dataset.open !== "true" || this.dismissed()) return

    this.openConfirmation(0)
  }

  openConfirmation(attempt) {
    if (attempt > 10) return

    const drawer = this.element.querySelector(".bottom-drawer")

    if (drawer && typeof drawer.showSheet === "function") {
      drawer.showSheet()
      return
    }

    setTimeout(() => this.openConfirmation(attempt + 1), 100)
  }

  submit(event) {
    if (!this.strongCandidatePresent() || this.dismissed()) return

    event.preventDefault()
    // The loading state is started by the submit buttons' own listeners on this same event, which
    // may run after this one, so the reset waits for the event to finish being handled.
    setTimeout(() => this.stopLoadingButtons(), 0)
    if (this.hasDrawerTriggerTarget) this.drawerTriggerTarget.click()
  }

  // A submit button starts its loading animation on the form's submit event, and nothing is
  // coming back to end it when the submission is held for the confirmation drawer.
  stopLoadingButtons() {
    this.element.querySelectorAll("button[type='submit']").forEach((button) => {
      button.dispatchEvent(new CustomEvent("loading-button:stop"))
    })
  }

  // The submitter says the catalog listing is a different product. Their answer travels with the
  // submission, so the same check on the server lets it through and the reviewer sees why.
  markDifferent() {
    this.dismissalTargets.forEach((field) => { field.disabled = false })

    if (!this.hasDistinctionTarget) return

    this.distinctionFieldTarget.hidden = false
    this.distinctionTarget.disabled = false
    this.distinctionTarget.required = true
    this.distinctionTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.distinctionTarget.focus()
    // The answer sits at the foot of the drawer, so revealing this field pushes its own submit
    // button past the bottom edge. Bring the whole block into view, not just the input focus lands on.
    this.distinctionFieldTarget.scrollIntoView({ behavior: "smooth", block: "end" })
  }

  strongCandidatePresent() {
    return this.hasResultsTarget && this.resultsTarget.dataset.strength === "strong"
  }

  dismissed() {
    return this.dismissalTargets.length > 0 && this.dismissalTargets.every((field) => !field.disabled)
  }
}
